import React from "react";

import { SLOT_SPRITE } from "./sprite";
import { dayOfWeek, dayNumber, shortDate } from "./format";
import "./slots.css";

/**
 * Coque commune aux écrans « créneau de collecte » et « créneau de
 * livraison » (design Checkout.dc.html, écrans 01 et 02) : retour et
 * progression, choix du jour et du créneau, récapitulatif des passages.
 *
 * Présentation seule : les règles (plancher, appariement journée/soirée,
 * marge du jour même) viennent de utils/slotSelection, via les écrans.
 */

export const Icon = ({ name }) => (
  <svg viewBox="0 0 512 512" aria-hidden="true">
    <use href={`#slot-${name}`} />
  </svg>
);

const Sprite = () => (
  <svg
    width="0"
    height="0"
    style={{ position: "absolute" }}
    aria-hidden="true"
    /* Contenu statique extrait du design, voir sprite.js. */
    dangerouslySetInnerHTML={{ __html: `<defs>${SLOT_SPRITE}</defs>` }}
  />
);

/**
 * @param {object}   step        { done, total, label } — jauge d'étapes.
 * @param {Array}    days        Jours construits par slotSelection.
 * @param {string}   dayKey      Jour affiché.
 * @param {string}   slotRaw     Créneau choisi, format API « 08:00-12:00 ».
 * @param {string}   slotsLabel  Suffixe de l'intitulé : « Créneaux de collecte ».
 * @param {Array}    infos       [{ icon, text }] — cadres d'information.
 * @param {string}   pickupSummary / deliverySummary  null → « À choisir ».
 */
const SlotScreen = ({
  t,
  step,
  onBack,
  title,
  lede,
  days,
  dayKey,
  onSelectDay,
  slotRaw,
  onSelectSlot,
  slotsLabel,
  infos = [],
  error,
  pickupSummary,
  deliverySummary,
  ctaLabel,
  ctaDisabled,
  onContinue,
}) => {
  const day = days.find((item) => item.key === dayKey) || null;

  return (
    <div className="wosh-slot">
      <Sprite />

      <div className="wosh-slot__top">
        <button
          type="button"
          className="wosh-slot__back"
          aria-label={t("Back")}
          onClick={onBack}
        >
          <Icon name="arrow-back" />
        </button>

        <div className="wosh-slot__progress">
          <div className="wosh-slot__segments" aria-hidden="true">
            {Array.from({ length: step.total }, (_, index) => (
              <span key={index} className={index < step.done ? "is-done" : ""} />
            ))}
          </div>
          <div className="wosh-slot__step">{step.label}</div>
        </div>
      </div>

      <div className="wosh-slot__head">
        <h1 className="wosh-slot__title">{title}</h1>
        <p className="wosh-slot__lede">{lede}</p>
      </div>

      <div className="wosh-slot__body">
        <main className="wosh-slot__card">
          {!days.length ? (
            <p className="wosh-slot__empty">
              {t("No slot is available for this address at the moment.")}
            </p>
          ) : (
            <>
              <div className="wosh-slot__days" role="radiogroup" aria-label={t("Day")}>
                {days.map((item) => {
                  const on = item.key === dayKey;

                  return (
                    <button
                      type="button"
                      role="radio"
                      aria-checked={on}
                      key={item.key}
                      className={`wosh-slot__day${on ? " wosh-slot__day--on" : ""}`}
                      onClick={() => onSelectDay(item.key)}
                    >
                      <span className="wosh-slot__dow">{dayOfWeek(item.key)}</span>
                      <span className="wosh-slot__num">{dayNumber(item.key)}</span>
                    </button>
                  );
                })}
              </div>

              {day && (
                <>
                  <div className="wosh-slot__eyebrow">
                    {shortDate(day.key)} — {slotsLabel}
                  </div>

                  <div className="wosh-slot__slots" role="radiogroup" aria-label={slotsLabel}>
                    {day.slots.map((slot) => {
                      const on = slot.raw === slotRaw;

                      return (
                        <button
                          type="button"
                          role="radio"
                          aria-checked={on}
                          key={slot.raw}
                          disabled={slot.disabled}
                          className={`wosh-slot__slot${on ? " wosh-slot__slot--on" : ""}`}
                          onClick={() => onSelectSlot(slot)}
                        >
                          <Icon name="time" />
                          <span className="wosh-slot__range">{slot.label}</span>
                          {on && <Icon name="check" />}
                          {slot.disabled && <span className="wosh-slot__na">N/A</span>}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {infos.map((info) => (
                <div className="wosh-slot__info" key={info.text}>
                  <Icon name={info.icon} />
                  <span>{info.text}</span>
                </div>
              ))}
            </>
          )}
        </main>

        <aside className="wosh-slot__aside">
          <div className="wosh-slot__aside-title">{t("Your visits")}</div>

          <div className="wosh-slot__passes">
            <div className="wosh-slot__pass">
              <span className="wosh-slot__pass-badge">
                <Icon name="cube" />
              </span>
              <div>
                <div className="wosh-slot__pass-label">{t("Pickup")}</div>
                <div
                  className={`wosh-slot__pass-value${
                    pickupSummary ? "" : " wosh-slot__pass-value--pending"
                  }`}
                >
                  {pickupSummary || t("To be chosen")}
                </div>
              </div>
            </div>

            <div className="wosh-slot__pass">
              <span className="wosh-slot__pass-badge">
                <Icon name="truck" />
              </span>
              <div>
                <div className="wosh-slot__pass-label">{t("Delivery")}</div>
                <div
                  className={`wosh-slot__pass-value${
                    deliverySummary ? "" : " wosh-slot__pass-value--pending"
                  }`}
                >
                  {deliverySummary || t("To be chosen")}
                </div>
              </div>
            </div>
          </div>

          {error && <p className="wosh-slot__error" role="alert">{error}</p>}

          <button
            type="button"
            className="wosh-btn"
            disabled={ctaDisabled}
            onClick={onContinue}
          >
            {ctaLabel}
          </button>
          <div className="wosh-slot__note">
            {t("Can be changed until 1 hour before the visit")}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default SlotScreen;
